import {
  useReactionScorePerTag,
  useTotalNegativeVideoReactions,
  useTotalNeutralVideoReactions,
  useTotalPositiveVideoReactions,
} from "@/store/selectors/userProfileSelectors";
import { Group, Space, Stack } from "@mantine/core";
import {
  IconHeartFilled,
  IconThumbDownFilled,
  IconThumbUpFilled,
} from "@tabler/icons-react";
import { Text } from "../ui/Text";
import { useStyles } from "@/styles/useStyles";
import { useMemo } from "react";
import { AdvizerTag, IAdvizerTag } from "../AdvizerTag";
import { ResourceConfigs } from "@/configs/resourceConfigs";
import { scoreByReaction } from "@/store/selectors/userProfileSelectors";
import { useStore } from "@/store";
import { reactionTypeToColor, reactionTypeToIcon } from "@/configs/reactions";

const tagTypeToLabel = {
  jobFunctions: "Job Roles",
  industries: "Industries",
  subjects: "Majors",
  traits: "Traits",
};

export function UserProfileReactions() {
  const { theme } = useStyles();
  const totalPositiveVideoReactions = useTotalPositiveVideoReactions();
  const totalNeutralVideoReactions = useTotalNeutralVideoReactions();
  const totalNegativeVideoReactions = useTotalNegativeVideoReactions();

  const iconSize = 32;

  const TopLevelStats = useMemo(() => {
    return (
      <Group gap="xl">
        <ReactionStatCard
          label="This fits me!"
          reactionType="positive"
          value={totalPositiveVideoReactions}
          color={reactionTypeToColor.positive}
          icon={
            <IconHeartFilled
              size={iconSize}
              color={theme.colors[reactionTypeToColor.positive][7]}
            />
          }
        />
        <ReactionStatCard
          label="Interesting"
          reactionType="neutral"
          value={totalNeutralVideoReactions}
          color={reactionTypeToColor.neutral}
          icon={
            <IconThumbUpFilled
              size={iconSize}
              color={theme.colors[reactionTypeToColor.neutral][7]}
            />
          }
        />
        <ReactionStatCard
          label="Not for me"
          reactionType="negative"
          value={totalNegativeVideoReactions}
          color={reactionTypeToColor.negative}
          icon={
            <IconThumbDownFilled
              size={iconSize}
              color={theme.colors[reactionTypeToColor.negative][6]}
            />
          }
        />
      </Group>
    );
  }, [
    theme.colors,
    totalNegativeVideoReactions,
    totalNeutralVideoReactions,
    totalPositiveVideoReactions,
  ]);

  return (
    <Stack gap={0} pt="md">
      {TopLevelStats}
      <Space h="xl" />
      <TagReactionSection tagType="subjects" />
      <Space h="xl" />
      <TagReactionSection tagType="jobFunctions" />
      <Space h="xl" />
      <TagReactionSection tagType="industries" />
    </Stack>
  );
}

function TagReactionSection(props: {
  tagType: "jobFunctions" | "industries" | "subjects" | "traits";
}) {
  const { scoreMap } = useReactionScorePerTag(props.tagType);
  const apiDataRaw = useStore((state) => state.advizeApiData[props.tagType]);
  const { theme } = useStyles();

  // Map tagType to ResourceConfig key
  const tagTypeToResourceConfigKey: Record<
    typeof props.tagType,
    keyof typeof ResourceConfigs
  > = {
    jobFunctions: "jobFunction",
    industries: "industry",
    subjects: "subject",
    traits: "trait",
  };
  const resourceConfigKey = tagTypeToResourceConfigKey[props.tagType];
  const ResourceIcon = ResourceConfigs[resourceConfigKey].Icon;

  // Create tags with scores, sorted by score (highest to lowest)
  const tagsWithScores = useMemo(() => {
    const apiData = apiDataRaw || [];
    const items: Array<{ tag: IAdvizerTag; score: number }> = [];

    Object.entries(scoreMap).forEach(([tagSlug, score]) => {
      // Find the tag in API data by slug
      const tagData = apiData.find((tag: any) => tag.slug === tagSlug);
      if (!tagData) return;

      items.push({
        tag: {
          id: tagData.id,
          title: tagData.title,
          type: resourceConfigKey as IAdvizerTag["type"],
          slug: tagSlug,
        },
        score,
      });
    });

    return items.sort((a, b) => b.score - a.score); // Sort highest to lowest
  }, [scoreMap, apiDataRaw, resourceConfigKey]);

  // Determine reaction icon based on score
  const getReactionIcon = (score: number) => {
    if (score > 0) return reactionTypeToIcon.positive;
    if (score < 0) return reactionTypeToIcon.negative;
    return reactionTypeToIcon.neutral;
  };

  // Determine reaction color based on score
  const getReactionColor = (score: number) => {
    if (score > 0) return reactionTypeToColor.positive;
    if (score < 0) return reactionTypeToColor.negative;
    return reactionTypeToColor.neutral;
  };

  return (
    <Stack gap={0}>
      <Group gap="xs" align="center">
        <ResourceIcon
          size={24}
          // color={theme.colors[ResourceConfigs[props.tagType].color][7]}
          color={theme.colors.text[7]}
        />
        <Text
          size="sm"
          titleStyle="h4"
          fw={700}
          c="text.7"
          // c={ResourceConfigs[props.tagType].color || "primary"}
          mt={3}
        >
          {tagTypeToLabel[props.tagType]}
        </Text>
      </Group>
      <Space h="xs" />
      <Group gap="sm">
        {tagsWithScores.length > 0 ? (
          tagsWithScores.map(({ tag, score }) => (
            <Group
              key={tag.id}
              gap={4}
              opacity={score < 0 ? 0.75 : score > 0 ? 1 : 0.85}
            >
              <AdvizerTag
                tag={tag}
                showScore={score}
                size={score > 0 ? "xl" : score < 0 ? "sm" : "lg"}
                disableClick
                hideIcon
                reactionIcon={getReactionIcon(score)}
                color={getReactionColor(score)}
              />
            </Group>
          ))
        ) : (
          <Text c="text.5">
            No reactions for any {tagTypeToLabel[props.tagType].toLowerCase()}{" "}
            yet.
          </Text>
        )}
      </Group>
    </Stack>
  );
}

export function ReactionStatCard(props: {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
  reactionType: "positive" | "neutral" | "negative";
}) {
  const { label, value, color, icon, reactionType } = props;
  const score = scoreByReaction[reactionType];
  return (
    <Stack gap={0} justify="center" align="center">
      <Text size="lg" c={color} opacity={0.75} fw={700}>
        {score > -1 ? "+" : "-"} {Math.abs(score)}
      </Text>
      <Group gap={4} align="center">
        {icon}
        <Text titleStyle="h1" size="sm" lh={1} fw={700} mt={4} c={color}>
          {value}
        </Text>
      </Group>

      <Group gap={4}>
        <Text size="xs" titleStyle="h5" c={`${color}.7`} fw={700}>
          {label}
        </Text>
      </Group>
    </Stack>
  );
}
